/**
 * The in-app-webview interstitial.
 *
 * A friend's invite link most often opens inside Instagram/Messenger/TikTok,
 * where passkeys usually fail. Detecting that *before* asking for a name means
 * the player never starts a ceremony that cannot finish. The copy-link button is
 * the fix that actually works everywhere; the deep link is a bonus where the
 * platform supports it.
 */
import { useState } from 'react'
import type { WebAuthnAvailability } from './passkey'

export function UnsupportedBrowser({ availability }: { availability: WebAuthnAvailability }): JSX.Element {
  const [copied, setCopied] = useState(false)
  const url = typeof location === 'undefined' ? '' : location.href
  const inApp = availability.kind === 'inAppBrowser'

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="page">
      <div className="wordmark">
        cards{' '}
        <span className="suit">
          <span className="s-s">♠</span>
          <span className="s-h">♥</span>
          <span className="s-d">♦</span>
          <span className="s-c">♣</span>
        </span>
      </div>
      <div className="live" style={{ marginTop: 'auto', marginBottom: 'auto' }}>
        <h2>{inApp ? 'Open this in Safari or Chrome' : 'This browser cannot hold a passkey'}</h2>
        <p>
          {inApp
            ? `${availability.app}'s built-in browser cannot create a passkey, and a passkey is how you sign in. Open this link in your normal browser and you are two taps from the table.`
            : 'Signing in needs a browser that supports passkeys — Safari, Chrome, Edge or Firefox on a current phone or desktop.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <button className="btn btn-primary btn-block" onClick={copy}>
            {copied ? 'Link copied' : 'Copy the link'}
          </button>
          {inApp ? (
            <a className="btn btn-secondary btn-block" href={safariUrl(url)}>
              Try opening it now
            </a>
          ) : null}
        </div>
        <p className="faint" style={{ marginTop: 14, wordBreak: 'break-all' }}>
          {url}
        </p>
      </div>
    </div>
  )
}

/** iOS honours `x-safari-https://`; Android honours an intent URL. Neither is
 *  reliable, which is why the copy button is the primary action. */
function safariUrl(url: string): string {
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return `x-safari-${url}`
  return `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`
}
